import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDiscardGuard } from '../hooks/useUnsavedChanges'

/**
 * Dialog Component - Vervangt prompt() en confirm()
 * Ondersteunt zowel message prop als children voor custom content
 */
export function Dialog({ isOpen, onClose, title, message, type = 'info', onConfirm, onCancel, confirmText, cancelText, children, size = 'md', isDirty = false, confirmOnClose = false }) {
  const { t } = useTranslation()
  const defaultConfirmText = confirmText || t('common.confirm', { defaultValue: 'OK' })
  const defaultCancelText = cancelText || t('common.cancel')
  const discardGuard = useDiscardGuard()

  // Brede heuristiek: als confirmOnClose aanstaat, markeer "aangeraakt" bij elke
  // veldwijziging binnen de modal; reset bij (her)openen.
  const [touched, setTouched] = useState(false)
  useEffect(() => { if (isOpen) setTouched(false) }, [isOpen])
  const guardDirty = isDirty || (confirmOnClose && touched)

  // Sluiten via X of overlay: waarschuw (R11-dialoog) bij niet-opgeslagen wijzigingen.
  const requestClose = async () => {
    if (!onClose) return
    if (await discardGuard(guardDirty)) onClose()
  }
  const onContentChange = confirmOnClose ? () => setTouched(true) : undefined
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const icon = {
    info: 'ℹ️',
    warning: '⚠️',
    error: '❌',
    success: '✅',
    question: '❓'
  }[type] || 'ℹ️'

  const iconColor = {
    info: 'text-blue-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
    success: 'text-green-500',
    question: 'text-blue-500'
  }[type] || 'text-blue-500'

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  }[size] || 'max-w-md'

  // Als children worden gebruikt, render custom content
  if (children) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          // Sluit alleen als je op de achtergrond klikt, niet op de dialog zelf
          if (e.target === e.currentTarget) {
            requestClose()
          }
        }}
      >
        <div 
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl ${sizeClasses} w-full max-h-[calc(100vh-2rem)] my-auto flex flex-col overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {title}
              </h3>
              {onClose && (
                <button
                  onClick={requestClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              )}
            </div>
          )}
          {/* Body scrollt binnen de max-hoogte; header blijft staan, footer (in
              children) blijft bereikbaar. Fix: lange modals vielen onderaan weg. */}
          {/* onChange (delegatie) voedt de confirmOnClose-heuristiek. */}
          <div className="flex-1 min-h-0 overflow-y-auto" onChange={onContentChange}>
            {children}
          </div>
        </div>
      </div>
    )
  }

  // Standaard message-based dialog
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${sizeClasses} w-full p-6`}>
        <div className="flex items-start mb-4">
          <div className={`flex-shrink-0 text-3xl ${iconColor}`}>
            {icon}
          </div>
          <div className="ml-4 flex-1">
            {title && (
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {title}
              </h3>
            )}
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {message}
            </p>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end mt-6">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
            >
              {defaultCancelText}
            </button>
          )}
          <button
            onClick={onConfirm || onClose}
            className={`px-4 py-2 rounded-lg font-semibold ${
              type === 'error' 
                ? 'bg-red-600 text-white hover:bg-red-700'
                : type === 'warning'
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : type === 'success'
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {defaultConfirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

// Default export voor backward compatibility
export default Dialog

/**
 * Input Dialog - Vervangt prompt()
 */
export function InputDialog({ isOpen, onClose, title, message, type = 'text', placeholder = '', defaultValue = '', onConfirm, onCancel }) {
  const { t } = useTranslation()
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, defaultValue])

  if (!isOpen) return null

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(value)
    }
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm()
    } else if (e.key === 'Escape') {
      onCancel?.()
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="mb-4">
          {title && (
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {title}
            </h3>
          )}
          <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">
            {message}
          </p>
          <input
            type={type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => {
              onCancel?.()
              onClose()
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            {t('common.confirm', { defaultValue: 'OK' })}
          </button>
        </div>
      </div>
    </div>
  )
}
